import type { ITableSPlansActive } from "@/components/SavePlans/types/SavePlansTypes";
import type { Contracts, ContractsUnderSameSubscription } from "@/components/Payments/RePaymentSubscription";
import type { TUpgradeMode, TUpgradeOptionDTO } from "@/services/UsersSubscriptionService";

// Reglas puras de la pantalla de mejora de suscripción. Las reglas de tarifa (opciones, recomendada,
// días restantes y precio a cobrar) las da el backend en GET subscriptionOptions/{usersSubscriptionId};
// aquí solo se interpreta esa respuesta y se prepara el pago. El backend es quien manda al cobrar.

export const MinimumGatewayAmountCents = 100;
// Prefijo del concepto del pago de una mejora. El backend reconoce la mejora en POST /payments por este
// concepto (SubscriptionRenewalCalculator.UpgradeSubjectPrefix): tiene que coincidir exactamente.
export const UpgradeSubjectPrefix = "Mejora de la Suscripción ";

// Línea de Elements usados en un contrato (cantidades positivas tal y como las introduce el usuario).
export type TElementLine =
{
    elementAmountId: number;
    water: number;
    ia: number;
    earth: number;
};

// Formato que espera /inespay y /inespayNoPrice: Elements consumidos en negativo.
export type TContractElementsPayload =
{
    elementAmountId: number;
    water: number;
    ia: number;
    earth: number;
};

type TUtcDateParts =
{
    year: number;
    month: number;
    day: number;
};

/**
 * Obtiene año, mes (0-11) y día de una fecha comparando solo fechas en UTC.
 * Una cadena ISO sin zona ("2027-03-15T00:00:00", como serializa Newtonsoft un DateTime) se toma
 * por su fecha literal, igual que hace el backend con EndDate.Date; si se parseara con new Date()
 * se interpretaría en hora local y en España podría caer en el día anterior en UTC.
 */
export const getUtcDateParts = (value: string | Date | null | undefined): TUtcDateParts | null =>
{
    if(value === null || value === undefined || value === "")
    {
        return null;
    }

    if(typeof value === "string")
    {
        const hasTimeZone = /(Z|[+-]\d{2}:?\d{2})$/i.test(value.trim());
        const isoMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
        if(isoMatch && !hasTimeZone)
        {
            return { year: Number(isoMatch[1]), month: Number(isoMatch[2]) - 1, day: Number(isoMatch[3]) };
        }
    }

    const date = value instanceof Date ? value : new Date(value);
    if(isNaN(date.getTime()))
    {
        return null;
    }

    return { year: date.getUTCFullYear(), month: date.getUTCMonth(), day: date.getUTCDate() };
};

/**
 * Euros a céntimos enteros, redondeando para absorber el error de coma flotante (19.99 * 100 = 1998.9999999999998).
 */
export const eurosToCents = (euros: number | null | undefined): number =>
{
    return typeof euros === "number" && !isNaN(euros) && euros > 0 ? Math.round(euros * 100) : 0;
};

/**
 * Importe final en céntimos: precio a cobrar (chargePrice en céntimos) menos los Elements (en euros). Nunca negativo.
 */
export const getFinalPriceCents = (chargeCents: number, elementsDiscountEuros: number): number =>
{
    const discountCents = Math.round((elementsDiscountEuros || 0) * 100);
    const finalCents = chargeCents - discountCents;

    return finalCents < 0 ? 0 : finalCents;
};

/**
 * Opción preseleccionada: la recomendada por el backend si está entre las opciones; si no, la primera.
 */
export const resolvePreselectedUpgradeOption = (options: TUpgradeOptionDTO[] | null | undefined, recommendedId: number | null | undefined): TUpgradeOptionDTO | null =>
{
    const list = options ?? [];
    return list.find((option) => option.id === recommendedId) ?? list[0] ?? null;
};

// Modos en los que se cobra la tarifa íntegra y la validez empieza de nuevo hoy (actúan como renovación).
const FullPriceUpgradeModes: TUpgradeMode[] = ["promotionToExperience", "monthlyToAnnual"];

/**
 * True si el modo cobra la tarifa completa con validez nueva (promotionToExperience, monthlyToAnnual).
 * Acepta cualquier cadena porque el valor llega del backend y puede ser uno que el front no conoce.
 */
export const isFullPriceUpgradeMode = (mode: string | null | undefined): boolean =>
{
    return FullPriceUpgradeModes.includes(mode as TUpgradeMode);
};

/**
 * True si el front sabe mostrar y cobrar este modo. Un modo desconocido no se trata como prorrateado:
 * la pantalla muestra un error y no deja pagar.
 */
export const isKnownUpgradeMode = (mode: string | null | undefined): boolean =>
{
    return mode === "prorated" || isFullPriceUpgradeMode(mode);
};

/**
 * Aviso de validez que se muestra antes de pagar, según el modo de mejora que indica el backend.
 * null si el modo es nulo o desconocido, o falta la fecha que hay que mostrar.
 */
export const getUpgradeNotice = (mode: string | null | undefined, endDate: string | null | undefined, newEndDate: string | null | undefined, subscriptionName: string | null | undefined): string | null =>
{
    if(mode === "prorated")
    {
        const endDateText = formatDateDdMmYyyy(endDate);
        return endDateText
            ? `La mejora de suscripción no amplía su validez. Tu suscripción seguirá caducando el ${endDateText}. Si quieres ampliarla, podrás renovarla después.`
            : null;
    }

    if(isFullPriceUpgradeMode(mode))
    {
        const newEndDateText = formatDateDdMmYyyy(newEndDate);
        return newEndDateText && subscriptionName
            ? `Se cobra la tarifa completa. Tu suscripción pasará a ${subscriptionName} y será válida desde hoy hasta el ${newEndDateText}.`
            : null;
    }

    return null;
};

const toPositiveInteger = (value: unknown): number | undefined =>
{
    const numeric = typeof value === "string" && value.trim() !== "" ? Number(value) : value;
    return typeof numeric === "number" && Number.isInteger(numeric) && numeric > 0 ? numeric : undefined;
};

/**
 * Id de la UsersSubscription a mejorar: el del 409 si se llega desde un límite superado; si no
 * (enlace directo), el userSubscriptionId del plan activo. undefined si no hay ninguno válido.
 */
export const resolveUsersSubscriptionId = (fromLimitExceeded: unknown, fromActivePlan: unknown): number | undefined =>
{
    return toPositiveInteger(fromLimitExceeded) ?? toPositiveInteger(fromActivePlan);
};

export type TCanPayUpgradeParams =
{
    selectedSubscriptionId?: number | null;
    usersSubscriptionId?: number | null;
    elementsValid: boolean;
    // upgrade.allowed del backend (cubre caducada, tarifas que no se mejoran, etc.).
    upgradeAllowed: boolean;
    isSubmitting: boolean;
};

/**
 * El botón Pagar solo se habilita con tarifa elegida, Elements válidos, mejora permitida por el backend,
 * id de la suscripción del usuario conocido y sin un envío en curso.
 */
export const canPayUpgrade = ({ selectedSubscriptionId, usersSubscriptionId, elementsValid, upgradeAllowed, isSubmitting }: TCanPayUpgradeParams): boolean =>
{
    return selectedSubscriptionId != null &&
        usersSubscriptionId != null &&
        elementsValid === true &&
        upgradeAllowed === true &&
        isSubmitting === false;
};

/**
 * La pasarela PSD2 no acepta importes entre 0,01 € y 0,99 €. Devuelve el mensaje de error o null.
 */
export const getGatewayAmountError = (finalPriceCents: number): string | null =>
{
    if(finalPriceCents > 0 && finalPriceCents < MinimumGatewayAmountCents)
    {
        return "El importe a pagar debe ser completamente 0 € o mayor de 1 € para poder procesar la transacción. Los valores entre 0,01 € y 1 € no son aceptados por la plataforma PSD2.";
    }

    return null;
};

/**
 * Concepto del pago de una mejora: "Mejora de la Suscripción {userId}-{subscriptionId}".
 * Mismo formato que BuildUpgradeSubject del backend.
 */
export const buildUpgradeSubject = (userId: number, subscriptionId: number): string =>
{
    return `${UpgradeSubjectPrefix}${userId}-${subscriptionId}`;
};

/**
 * Elements usados en el formato de /inespay: en negativo, como hace RePaymentSubscription.
 */
export const buildContractsElements = (lines: TElementLine[]): TContractElementsPayload[] =>
{
    // Se evita el -0 (JSON lo serializa como 0, pero así queda limpio también en memoria).
    const negate = (value: number) => (value ? -1 * value : 0);

    return lines.map((line) =>
    ({
        elementAmountId: line.elementAmountId,
        water: negate(line.water),
        ia: negate(line.ia),
        earth: negate(line.earth),
    }));
};

/**
 * Fecha dd/mm/aaaa (fecha UTC; una ISO sin zona se toma por su fecha literal, como EndDate.Date del backend).
 */
export const formatDateDdMmYyyy = (value: string | Date | null | undefined): string =>
{
    const parts = getUtcDateParts(value);
    if(parts === null)
    {
        return "";
    }

    return `${String(parts.day).padStart(2, "0")}/${String(parts.month + 1).padStart(2, "0")}/${parts.year}`;
};

/**
 * Construye el state.contracts que necesita /Repayment (RePaymentSubscription) para una suscripción,
 * con el mismo mapeo que TableActiveSavingPlans (no hay helper compartido): contratos de todos los
 * planes con esa suscripción y contrato de Finametrix, y cabecera del primer plan activo (status 4).
 * Devuelve null si no hay datos suficientes para que RePaymentSubscription los acepte.
 */
export const buildRepaymentContracts = (plans: ITableSPlansActive[] | null | undefined, userSubscriptionId: number): ContractsUnderSameSubscription | null =>
{
    const subscriptionPlans = (plans ?? []).filter((plan) => plan.userSubscriptionId === userSubscriptionId);
    const contractInfo: Contracts[] = subscriptionPlans
        .filter((plan) => plan.finametrixContract !== null)
        .map((plan) =>
        ({
            contract: plan.finametrixContract,
            elementAmountId: plan.elementAmountId,
            elementsIA: plan.elementsIA,
            elementsWater: plan.elementsWater,
            elementsEarth: plan.elementsEarth,
            savingPlanName: plan.planName,
            hasElements: plan.hasElements,
            isSubscriptionRenewalBeforeExpiration: plan.isSubscriptionRenewalBeforeExpiration,
            savingPlanId: plan.id,
        }));

    const row = subscriptionPlans.find((plan) => plan.status === 4) ?? subscriptionPlans[0];
    if(!row || contractInfo.length === 0 || !row.validDate)
    {
        return null;
    }

    return {
        subscriptionInfo: contractInfo,
        dateOfOutdate: row.validDate,
        outdateRecharge: row.outdateRecharge ?? 0,
        subscriptionId: row.subscriptionId ?? 0,
        subscriptionType: row.subscriptionType ?? "",
        subscriptionLimit: row.subscriptionLimit ?? 0,
        subscriptionName: row.subscriptionName ?? "",
        subscriptionPeriod: row.subscriptionPeriod ?? "",
        subscriptionPrice: row.subscriptionPrice ?? 0,
        userSubscriptionId: row.userSubscriptionId ?? userSubscriptionId,
    };
};
